import React, { useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Menu,
  X,
  User,
  LogOut
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import SubscriptionStatus from './SubscriptionStatus';
import NotificationBell, { NotificationBellRef } from './NotificationBell';
import EmailVerificationBanner from './EmailVerificationBanner';
import logoImage from './Dark Green and Sea Green Modern Gradient Pharmacy Health Logo (500 x 200 px).png';
import { supabase } from '../lib/supabase';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  href: string;
  label: string;
  requiresAuth?: boolean;
  cdmoOnly?: boolean;
  adminOnly?: boolean;
  /** Only the superadmin sees this. A plain admin does not, and neither does anyone else. */
  superadminOnly?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const { user, signOut, isAdmin, isSuperadmin } = useAuth();
  const location = useLocation();
  const notificationBellRef = useRef<NotificationBellRef>(null);
  const [userRole, setUserRole] = React.useState<string | null>(null);

  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  React.useEffect(() => {
    const fetchUserRole = async () => {
      if (!user?.id) return;
      try {
        const { data: userProfile, error: profileError } = await supabase
          .from('users')
          .select('role')
          .eq('id', user.id)
          .single();

        if (profileError) {
          console.error('Error fetching user role:', profileError);
        } else {
          setUserRole(userProfile?.role);
        }
      } catch (error) {
        console.error('Unexpected error fetching user metadata:', error);
      }
    };

    fetchUserRole();
  }, [user]);

  const navItems = React.useMemo(() => {
    const items: NavItem[] = [
      { href: '/', label: 'Home' },
      {
        href: user ? '/matchmaking' : '/startup-signup',
        label: 'Find CDMOs',
        requiresAuth: true
      },
      {
        href: userRole === 'cdmo' ? '/cdmo-dashboard' : '/startup-dashboard',
        label: userRole === 'cdmo' ? 'CDMO Dashboard' : 'Dashboard',
        requiresAuth: true,
      },
      {
        href: '/campaign',
        label: 'Campaign',
        requiresAuth: true,
        adminOnly: true,
      },
      { href: '/pricing', label: 'Pricing' },
      { href: '/contact', label: 'Contact' },
      {
        href: '/admin-access',
        label: 'Admin access',
        requiresAuth: true,
        superadminOnly: true,
      }
    ];

    // Filter items based on authentication and role requirements
    return items.filter(item => {
      // Hide items that require authentication if user is not logged in
      if (item.requiresAuth && !user) {
        return false;
      }

      // Hide CDMO-only items if user is not a CDMO
      if (item.cdmoOnly) {
        return userRole === 'cdmo';
      }

      if (item.adminOnly) {
        return isAdmin;
      }

      if (item.superadminOnly) {
        return isSuperadmin;
      }

      return true;
    });
  }, [user, userRole, isAdmin, isSuperadmin]);

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsMenuOpen(false);
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        role="navigation"
        aria-label="Main navigation"
        className={`fixed top-0 left-0 right-0 z-50 bg-purple-500/10 backdrop-blur-md transition-shadow duration-300 ${
          scrolled ? 'shadow-lg' : ''
          }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/*
              Logo. shrink-0 and max-w-none matter here: the header is a flex row, so a wider nav
              compresses a flex sibling, and an image with w-auto loses its aspect ratio when that
              happens rather than keeping its width.
            */}
            <Link to="/" className="flex items-center space-x-2 group shrink-0">
              <motion.img
                whileHover={{ scale: 1.05 }}
                src={logoImage}
                alt="Pharma Connect Navigator"
                className="h-12 md:h-14 w-auto max-w-none shrink-0 transition-transform duration-300"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
              {navItems.map((item, index) => {
                // If user is logged in and trying to access signup page, redirect to matchmaking
                const handleClick = (e: React.MouseEvent) => {
                  if (user && item.href === '/startup-signup') {
                    e.preventDefault();
                    window.location.href = '/matchmaking';
                  }
                };

                return (
                  <Link
                    key={`${item.href}-${index}`}
                    to={item.href}
                    onClick={handleClick}
                    className={`text-sm lg:text-base font-medium whitespace-nowrap transition-colors duration-300 hover:text-electric-blue relative ${
                      (location.pathname === item.href) ||
                        (item.requiresAuth && !user && location.pathname === '/startup-signup')
                        ? 'text-deep-indigo'
                        : 'text-slate-gray'
                      }`}
                  >
                    {item.label}
                    {((location.pathname === item.href) ||
                      (item.requiresAuth && !user && location.pathname === '/startup-signup')) && (
                        <motion.div
                          layoutId="activeTab"
                          className="absolute -bottom-1 left-0 right-0 h-1 bg-gradient-to-r from-deep-indigo to-electric-blue rounded-full"
                        />
                      )}
                  </Link>
                );
              })}

              {/* User Menu for Desktop */}
              {user && (
                <div className="flex items-center space-x-4 ml-4 pl-4 border-l border-slate-gray/30">
                  {
                    userRole === 'cdmo' && (
                      <SubscriptionStatus />
                    )
                  }
                  <NotificationBell ref={notificationBellRef} />
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-gradient-primary rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-sm font-medium text-charcoal">
                      {user.user_metadata?.username || user.email?.split('@')[0]}
                    </span>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleSignOut}
                    className="p-2 text-slate-gray hover:text-electric-blue transition-colors duration-300 rounded-lg hover:bg-light-gray"
                    title="Sign Out"
                  >
                    <LogOut className="w-4 h-4" />
                  </motion.button>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-slate-gray hover:text-electric-blue transition-colors duration-300 rounded-lg hover:bg-light-gray min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{
            opacity: isMenuOpen ? 1 : 0,
            height: isMenuOpen ? 'auto' : 0
          }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="md:hidden bg-white/95 backdrop-blur-md overflow-hidden"
        >
          <div className="px-4 py-3 space-y-1">
            {navItems.map((item, index) => {
              // If user is logged in and trying to access signup page, redirect to matchmaking
              const handleClick = (e: React.MouseEvent) => {
                if (user && item.href === '/startup-signup') {
                  e.preventDefault();
                  window.location.href = '/matchmaking';
                }
                setIsMenuOpen(false);
              };

              return (
                <Link
                  key={`${item.href}-${index}`}
                  to={item.href}
                  onClick={handleClick}
                  className={`block px-4 py-3 text-base font-medium transition-colors duration-300 hover:text-electric-blue hover:bg-light-gray rounded-lg min-h-[44px] flex items-center ${
                    (location.pathname === item.href) ||
                      (item.requiresAuth && !user && location.pathname === '/startup-signup')
                      ? 'text-deep-indigo bg-light-gray'
                      : 'text-slate-gray'
                    }`}
                >
                  {item.label}
                </Link>
              );
            })}

            {/* User Menu for Mobile */}
            {user && (
              <div className="border-t border-slate-gray/20 pt-3 mt-3">
                <div className="px-4 py-2">
                  <SubscriptionStatus />
                  <NotificationBell ref={notificationBellRef} />
                </div>
                <div className="px-4 py-2 flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-primary rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-medium text-charcoal">
                    {user.user_metadata?.username || user.email?.split('@')[0]}
                  </span>
                </div>
                <button
                  onClick={handleSignOut}
                  className="w-full text-left px-4 py-3 text-base font-medium text-slate-gray hover:text-electric-blue hover:bg-light-gray rounded-lg min-h-[44px] flex items-center"
                >
                  <LogOut className="w-4 h-4 mr-3" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </nav>

      {/* Email Verification Banner */}
      <EmailVerificationBanner />

      {/* Main Content */}
      <main className="pt-16">
        {children}
      </main>
    </div>
  );
};

export default Layout;
